import { Container, Row, Col } from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DateInputForm, LoadingSpinner, ModalMessage } from "../../components";
import { useEffect, useState } from "react";
import { getStorageToken, useLogout } from "../../utilities";
import { useSelector } from "react-redux";
import { fetchMonthlyOrdersApi } from "../../api/ordersApi";

export default function AdminAnalytics() {
  // displays analytics charts related to sales data for the admin
  // for the dates selected in the date input form

  const initialDate2 = new Date().toLocaleDateString("en-CA");
  const initialDate1 = new Date(
    new Date().setMonth(new Date().getMonth() - 3)
  ).toLocaleDateString("en-CA"); //3 months ago

  const { stayLoggedIn } = useSelector((state) => state.user);
  const logout = useLogout();

  const [date1, setDate1] = useState(initialDate1);
  const [date2, setDate2] = useState(initialDate2);
  const [salesByMonth, setSalesByMonth] = useState([]);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showCharts, setShowCharts] = useState(false);
  const [inputError, setInputError] = useState("");
  const [updateCharts, setUpdateCharts] = useState(true);

  useEffect(() => {
    const fetchDateRangeSales = async () => {
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await fetchMonthlyOrdersApi(token, date1, date2);
        return res;
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
        setShowCharts(true);
      }
    };

    if (updateCharts) {
      setUpdateCharts(false);
      setIsLoading(true);
      fetchDateRangeSales()
        .then((res) => {
          setSalesByMonth(res.salesByMonth);
          setSalesByCategory(res.salesByCategory);
          setIsLoading(false);
        })
        .catch((error) =>
          setErrorMsg("Could not load data. Please try again later.")
        );
    }
  }, [updateCharts, date1, date2, stayLoggedIn, logout]);

  const handleDateRangeClick = async () => {
    if (!date1 || !date2) {
      setInputError("Please enter both a start date and an end date.");
    } else if (new Date(date1) > new Date(date2)) {
      setInputError("End date must be greater than start date.");
    } else {
      setInputError("");
      setUpdateCharts(true);
    }
  };
  

  const COLORS = ['#1d48bc', '#0a205a'];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, _id }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {/* {`${(percent * 100).toFixed(0)}%`}  */}
        {`${_id}`}
      </text>
    );
  };


  return (
    <Container className="mt-5">
      <h1 className="mb-5">Analytics</h1>
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {isLoading ? <LoadingSpinner /> : null}
      <Row>
        <Col md={8} xl={6}>
          {/* DATE INPUT FORM */}
          <DateInputForm
            date1={date1}
            date2={date2}
            setDate1={setDate1}
            setDate2={setDate2}
            handleDateRangeClick={handleDateRangeClick}
            inputError={inputError}
          />
        </Col>
      </Row>
      {showCharts && (
        <Row>
          <Col>
            <h4>
              Summary for{" "}
              <div style={{display: "block", marginTop: "6px"}}>
              <span style={{ fontStyle: "italic", fontWeight: "bolder"}}>
                {date1}{" "}
              </span>{" "}
              to{" "}
              <span style={{ fontStyle: "italic", fontWeight: "bolder" }}>
                {date2}
              </span>
              </div>
            </h4>

            {/* LINE CHART - sales per month */}
            <h5 className="mt-5 mb-4" style={{ textDecoration: "underline" }}>
              Monthly Sales Revenue
            </h5>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                width={500}
                height={300}
                data={salesByMonth}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="_id"
                  label={{
                    value: "MONTH",
                    offset: 50,
                    position: "insideBottomRight",
                    allowDuplicateCategory: false,
                  }}
                />
                <YAxis
                  label={{
                    value: "SALES $",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                <Legend verticalAlign="bottom" height={36} />

                <Line
                  type="monotone"
                  dataKey="monthlyTotal"
                  stroke="#1d48bc"
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>

            {/* BAR CHART -nr of orders */}
            <h5 className="mt-5 mb-4" style={{ textDecoration: "underline" }}>
              Monthly Number of Orders
            </h5>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                width={400}
                height={400}
                data={salesByMonth}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" />
                <YAxis />
                <Tooltip formatter={(value) => `${value.toFixed(0)}`} />
                <Legend />
                <Bar dataKey="nrOfOrders" fill="#1d48bc" />
              </BarChart>
            </ResponsiveContainer>

            {/* PIE CHART - by category */}
            <h5 className="mt-5 mb-4" style={{ textDecoration: "underline" }}>
              Total Sales by Category
            </h5>
            {salesByCategory && salesByCategory.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                    <PieChart  {...{overflow: "visible"}}>
                  <Pie
                    dataKey="totalAmount"
                    isAnimationActive={false}
                    data={salesByCategory}
                    cx="50%"
                    cy="50%"
                    outerRadius={180}
                    // fill="#1d48bc"
                    labelLine={false}

                    // label={({ _id }) => _id} // Display category name as label
                    label={renderCustomizedLabel}

                  >     {salesByCategory.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
            </Pie>
                  <Tooltip
                    formatter={(value, name, entry) => [
                      `$${entry.payload.totalAmount.toFixed(2)}`,
                      entry.payload._id,
                    ]}
                    label=""
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p>No sales for the selected period</p>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
}